import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import Molecule from "@/components/Molecule";
import DnaHelix from "@/components/DnaHelix";
import ProductCard from "@/components/ProductCard";
import { ButtonPrimary, ButtonSecondary } from "@/components/Buttons";
import api from "@/lib/api";
import {
    FlaskConical,
    Package,
    Layers,
    Microscope,
    FileCheck2,
    Scale,
    Eye,
    ShieldCheck,
    FileText,
    ArrowRight,
    Loader2,
} from "lucide-react";

const CATEGORY_IMAGES = {
    "Research Compounds": "/assets/category-peptides.jpg",
    "Raw Cosmetic Grade": "/assets/category-cosmetic.jpg",
    "Laboratory Supplies": "/assets/category-lab.jpg",
};

const TRUST = [
    { icon: FlaskConical, title: "Third-Party", sub: "Tested" },
    { icon: ShieldCheck, title: "Quality", sub: "Materials" },
    { icon: FileText, title: "COA", sub: "Documentation" },
    { icon: Package, title: "Research", sub: "Use Only" },
];

const PATHWAY = [
    { icon: Package, title: "Research Material", text: "Sourced from vetted suppliers and received under controlled conditions." },
    { icon: Layers, title: "Batch / Lot", text: "Every material is tied to a documented batch or lot reference." },
    { icon: Microscope, title: "Independent Testing", text: "Verified by third-party laboratories with no stake in the outcome." },
    { icon: FileCheck2, title: "Certificate of Analysis", text: "Results recorded in a COA — retained, organised and available." },
];

const WHY = [
    { icon: Scale, label: "Quality" },
    { icon: Eye, label: "Transparency" },
    { icon: ShieldCheck, label: "Independent Testing" },
    { icon: FileText, label: "Documentation" },
    { icon: Microscope, label: "Research Integrity" },
];

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [categories] = useState(["Research Compounds", "Raw Cosmetic Grade", "Laboratory Supplies"]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/materials", { params: { featured: true } })
            .then((res) => setFeatured(res.data))
            .catch(() => setFeatured([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="home-page">
            {/* Editorial hero */}
            <section className="relative pt-32 pb-24 sm:pt-44 sm:pb-32 px-6 lg:px-8 overflow-hidden">
                <div className="absolute inset-0 hero-vignette" />
                <Molecule className="absolute -right-20 top-16 h-[400px] w-[400px] text-gold/30 hidden lg:block" />
                <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="eyebrow">Precision / Purity / Purpose</p>
                            <h1 className="mt-9 font-serif uppercase text-[2.6rem] sm:text-6xl lg:text-[4.35rem] font-medium tracking-[0.015em] text-brand leading-[1.08]">
                                Research today
                                <span className="block">for a <span className="text-wine">stronger</span></span>
                                <span className="block">tomorrow.</span>
                            </h1>
                            <div className="wine-rule mt-10" />
                            <p className="mt-9 text-base sm:text-lg text-mist leading-relaxed max-w-xl">
                                High-quality research materials backed by independent testing and documentation.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="mt-12 flex flex-col sm:flex-row gap-4">
                                <ButtonPrimary to="/materials" data-testid="hero-shop-btn">
                                    Shop Products
                                </ButtonPrimary>
                                <ButtonSecondary to="/standards" data-testid="hero-standards-btn">
                                    Our Standards
                                </ButtonSecondary>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15} className="lg:col-span-5">
                        <div className="relative">
                            <DnaHelix className="absolute -left-24 top-1/2 -translate-y-1/2 h-[400px] w-auto text-brand/25 hidden lg:block" />
                            <div className="absolute -inset-3 translate-x-4 translate-y-4 bg-wine/10" aria-hidden="true" />
                            <div className="absolute -inset-3 border border-gold/40" aria-hidden="true" />
                            <img
                                src="/assets/hero-scene.jpg"
                                alt="Grayhaven Research vial in a laboratory setting"
                                className="relative w-full h-[360px] sm:h-[460px] lg:h-[540px] object-cover"
                                data-testid="hero-product-image"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Trust bar */}
            <section className="border-y border-stone-200 bg-white/60">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {TRUST.map((item) => (
                        <div key={item.title + item.sub} className="flex items-center gap-4" data-testid={`trust-${item.title.toLowerCase()}`}>
                            <item.icon className="h-6 w-6 text-gold shrink-0" strokeWidth={1.25} />
                            <p className="text-[0.6rem] uppercase tracking-[0.25em] font-semibold text-brand leading-relaxed">
                                {item.title}
                                <span className="block text-slate-500">{item.sub}</span>
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Category navigation — photographic editorial tiles */}
            <section className="py-20 sm:py-24 px-6 lg:px-8" data-testid="category-nav-section">
                <div className="max-w-7xl mx-auto">
                    <Reveal className="flex items-end justify-between gap-6">
                        <div>
                            <p className="eyebrow">Browse The Catalog</p>
                            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-light tracking-tight text-brand">
                                Research Categories
                            </h2>
                        </div>
                        <div className="gold-hairline hidden sm:block flex-1 mb-3" />
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {categories.map((category, i) => (
                            <Reveal key={category} delay={i * 0.08}>
                                <Link
                                    to={`/materials?category=${encodeURIComponent(category)}`}
                                    data-testid={`category-tile-${category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="group block bg-white border border-stone-200 overflow-hidden hover:border-wine/40 transition-colors duration-300"
                                >
                                    <div className="relative h-48 sm:h-56 overflow-hidden">
                                        <img
                                            src={CATEGORY_IMAGES[category]}
                                            alt={category}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                        />
                                        <span className="absolute top-4 right-4 text-[0.6rem] tracking-[0.3em] font-semibold text-white bg-brand/60 backdrop-blur-sm px-2.5 py-1.5">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <div className="p-6 flex items-end justify-between gap-4">
                                        <h3 className="font-serif text-2xl font-light text-brand">{category}</h3>
                                        <span className="inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.25em] font-semibold text-wine group-hover:text-wine-deep transition-colors duration-300 shrink-0">
                                            View Products <ArrowRight className="h-3.5 w-3.5" />
                                        </span>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Dark editorial band A */}
            <section className="relative bg-charcoal py-24 sm:py-32 px-6 lg:px-8 overflow-hidden">
                <DnaHelix className="absolute right-8 lg:right-24 top-1/2 -translate-y-1/2 h-[360px] w-auto text-white/15 hidden md:block" />
                <div className="max-w-7xl mx-auto relative">
                    <Reveal>
                        <p className="eyebrow-light">The Grayhaven Standard</p>
                        <h2 className="mt-7 font-serif uppercase text-3xl sm:text-4xl lg:text-[3.4rem] font-medium tracking-[0.02em] text-[#F8F4EE] leading-[1.12] max-w-3xl">
                            Quality without
                            <span className="block text-gold">compromise.</span>
                        </h2>
                        <div className="mt-9 h-px w-16 bg-gold/70" />
                        <p className="mt-8 text-sm sm:text-base text-slate-300/85 leading-relaxed max-w-xl">
                            Defined sourcing standards, independent verification, and documentation for every
                            material — because research deserves evidence, not assurances.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Featured research materials */}
            <section className="py-24 sm:py-28 px-6 lg:px-8 bg-white/70 border-y border-stone-200">
                <div className="max-w-7xl mx-auto">
                    <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                        <div>
                            <p className="eyebrow">Catalogue</p>
                            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-light tracking-tight text-brand">
                                Featured Research Materials
                            </h2>
                        </div>
                        <Link
                            to="/materials"
                            data-testid="featured-view-all-link"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-wine hover:text-wine-deep transition-colors duration-300 shrink-0"
                        >
                            View Full Catalogue <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>

                    {loading ? (
                        <div className="flex items-center gap-3 text-mist text-sm py-16">
                            <Loader2 className="h-4 w-4 animate-spin text-gold" /> Loading catalogue…
                        </div>
                    ) : (
                        <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-6 xl:grid-cols-3" data-testid="featured-materials-grid">
                            {featured.slice(0, 6).map((material, i) => (
                                <Reveal key={material.id} delay={Math.min(i * 0.06, 0.25)}>
                                    <ProductCard material={material} />
                                </Reveal>
                            ))}
                        </div>
                    )}
                    <Reveal className="mt-10">
                        <p className="text-xs leading-relaxed text-brand0 max-w-2xl">
                            Catalog entries marked “Placeholder” are reserved for the Grayhaven inventory currently
                            being finalised. All materials are supplied strictly for laboratory research use only.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Helix divider */}
            <div className="flex justify-center py-4 overflow-hidden" aria-hidden="true">
                <DnaHelix horizontal className="h-28 w-auto text-brand/20" />
            </div>

            {/* Tested. Documented. Transparent. */}
            <section className="relative py-24 sm:py-32 px-6 lg:px-8 overflow-hidden" data-testid="pathway-section">
                <DnaHelix className="absolute -left-10 bottom-0 h-[380px] w-auto text-brand/10 hidden lg:block" />
                <div className="max-w-7xl mx-auto relative">
                    <Reveal className="max-w-2xl">
                        <p className="eyebrow">Our Quality System</p>
                        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-brand">
                            Tested. Documented. <span className="italic text-gold">Transparent.</span>
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-mist">
                            Every material follows the same documented pathway — from sourcing to an independently
                            verified Certificate of Analysis. Nothing enters the catalog without it.
                        </p>
                    </Reveal>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-0">
                        {PATHWAY.map((step, i) => (
                            <Reveal key={step.title} delay={i * 0.1}>
                                <div className="relative md:px-6 md:first:pl-0 md:last:pr-0" data-testid={`pathway-step-${i}`}>
                                    {i < PATHWAY.length - 1 && (
                                        <span
                                            className="hidden md:block absolute top-7 left-[calc(50%+2.5rem)] right-[calc(-50%+2.5rem)] h-px bg-gold/25"
                                            aria-hidden="true"
                                        />
                                    )}
                                    <div className="flex md:flex-col items-start md:items-center gap-5 md:text-center">
                                        <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-gold/40 bg-ink">
                                            <step.icon className="h-6 w-6 text-gold" strokeWidth={1.25} />
                                        </span>
                                        <div>
                                            <p className="text-[0.6rem] uppercase tracking-[0.25em] text-brand0">
                                                Step {i + 1}
                                            </p>
                                            <h3 className="mt-1 font-serif text-xl font-light text-brand">{step.title}</h3>
                                            <p className="mt-2 text-xs leading-relaxed text-mist md:max-w-[220px] md:mx-auto">
                                                {step.text}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal className="mt-16">
                        <ButtonSecondary to="/testing" data-testid="pathway-testing-btn">
                            Testing & Documentation
                        </ButtonSecondary>
                    </Reveal>
                </div>
            </section>

            {/* Why Grayhaven — concise */}
            <section className="py-20 px-6 lg:px-8 bg-white/70 border-y border-stone-200">
                <div className="max-w-7xl mx-auto">
                    <Reveal className="text-center">
                        <p className="eyebrow">Why Grayhaven</p>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        {WHY.map((item, i) => (
                            <Reveal key={item.label} delay={i * 0.06}>
                                <div
                                    data-testid={`why-item-${i}`}
                                    className="flex flex-col items-center gap-3 border border-slate-800/70 rounded-sm px-4 py-7 hover:border-gold/40 transition-colors duration-300"
                                >
                                    <item.icon className="h-6 w-6 text-gold" strokeWidth={1.25} />
                                    <span className="text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-slate-700 text-center">
                                        {item.label}
                                    </span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-10 text-center">
                        <Link
                            to="/about"
                            data-testid="why-about-link"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-wine hover:text-wine-deep transition-colors duration-300"
                        >
                            Learn more about Grayhaven <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* Dark editorial band B */}
            <section className="relative bg-brand py-24 sm:py-28 px-6 lg:px-8 overflow-hidden">
                <Molecule className="absolute left-6 lg:left-20 bottom-4 h-52 w-52 text-gold/20 hidden md:block" />
                <div className="max-w-7xl mx-auto relative text-center">
                    <Reveal>
                        <p className="eyebrow-light">One Standard</p>
                        <h2 className="mt-6 font-serif uppercase text-3xl sm:text-4xl lg:text-[3.2rem] font-medium tracking-[0.02em] text-[#F8F4EE] leading-[1.12]">
                            Same purpose.
                            <span className="block text-gold">Greater possibilities.</span>
                        </h2>
                        <Link
                            to="/about"
                            data-testid="band-about-link"
                            className="mt-9 inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.25em] font-semibold text-gold hover:text-gold-bright transition-colors duration-300"
                        >
                            The Grayhaven Story <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 sm:py-28 px-6 lg:px-8">
                <Reveal className="max-w-3xl mx-auto text-center">
                    <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-brand">
                        Speak with Grayhaven
                    </h2>
                    <p className="mt-6 text-base leading-relaxed text-mist">
                        Questions about materials, testing, or documentation? We welcome them — transparency starts
                        with conversation.
                    </p>
                    <div className="mt-10">
                        <ButtonPrimary to="/contact" data-testid="home-cta-contact-btn">
                            Contact Grayhaven
                        </ButtonPrimary>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
